import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import * as XLSX from 'xlsx'

const fmt = v => v==null?'—':Number(v).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})
const fmtC = v => v==null?'':Number(v).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})
function monthKey(d){return d.toISOString().slice(0,7)}
function monthLabel(ym){const[y,m]=ym.split('-');return['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1]+" '"+y.slice(2)}
function firstOfMonth(ym){return ym+'-01'}

// item_type keys
const INVOICED_ROWS  = ['adv_payment','gross_invoice','adv_recovery','retention_ded']
const COLLECT_ROWS   = ['adv_collection','progress_collection','retention_collection']
const PAYMENT_ROWS   = ['pay_sub_supplier','pay_payroll','pay_others']
const LABELS = {
  adv_payment:           'Advanced Payment',
  gross_invoice:         'Gross Invoice',
  adv_recovery:          'Advance Recovery',
  retention_ded:         'Retention Deduction',
  net_invoice:           'Net Invoice',
  adv_collection:        'Advanced Payment Collection',
  progress_collection:   'Progress Invoice Collection (Net)',
  retention_collection:  'Retention Collection',
  total_collection:      'Total Collection',
  pay_sub_supplier:      'Subcontractor / Supplier',
  pay_payroll:           'Payroll',
  pay_others:            'Others',
  total_payment:         'Total Payments',
  finance_fees:          'Finance Fees',
  net_position:          'Net Position',
  opening:               'Opening Balance',
  project_bank:          'Project Bank Balance',
}
const NEGATIVE_ROWS = new Set(['adv_recovery','retention_ded'])
const COMPUTED_ROWS = new Set(['net_invoice','total_collection','total_payment','net_position','project_bank','pay_sub_supplier','pay_payroll','pay_others','finance_fees'])
const SECTION_HEADERS = { adv_payment:'INVOICED', adv_collection:'COLLECTION inc VAT', pay_sub_supplier:'PAYMENTS', finance_fees:'FINANCE FEES', '__net_section':'NET POSITION & BANK' }

export default function CashFlowExpectation({ projectId }) {
  const [settings, setSettings]         = useState(null)
  const [showSettings, setShowSettings] = useState(false)
  const [settingsForm, setSettingsForm] = useState({ project_start_month:'', project_end_month:'', adv_recovery_pct:10, retention_pct:5, collection_lag_months:2 })
  const [months, setMonths]             = useState([])

  // totals: { [item_type]: { total_at_completion, till_period_amount, till_period_month } }
  const [totals, setTotals]             = useState({})
  const [showTotalsModal, setShowTotalsModal] = useState(false)
  const [totalsForm, setTotalsForm]     = useState({}) // editing

  // monthly dist for manual rows
  const [dist, setDist]                 = useState({})
  const [dirty, setDirty]               = useState({})
  const [saving, setSaving]             = useState(false)

  // opening balance — single value entered by user
  const [openingBalance, setOpeningBalance] = useState(0)
  const [openingDirty, setOpeningDirty]     = useState(false)

  // read from other tables
  const [cashOutDist, setCashOutDist]   = useState({}) // { [ym]: { sub_supplier, payroll, others } }
  const [financeFees, setFinanceFees]   = useState({}) // { [ym]: total }

  // packages to determine payment type mapping
  const [pkgPayTypes, setPkgPayTypes]   = useState({}) // { [cf_package]: 'sub_supplier'|'payroll'|'others' }

  const [loading, setLoading]           = useState(true)

  useEffect(()=>{ if(projectId) loadAll(projectId) },[projectId])

  async function loadAll(pid){
    setLoading(true)
    await loadSettings(pid)
    await Promise.all([loadTotals(pid), loadDist(pid), loadCashOutDist(pid), loadFinanceFees(pid), loadPkgPayTypes(pid)])
    setLoading(false)
  }

  async function loadSettings(pid){
    const{data}=await supabase.from('cashflow_settings').select('*').eq('project_id',pid).maybeSingle()
    setSettings(data)
    if(data?.project_start_month && data?.project_end_month){
      setMonths(buildMonths(data.project_start_month,data.project_end_month))
      setSettingsForm({
        project_start_month: data.project_start_month.slice(0,7),
        project_end_month:   data.project_end_month.slice(0,7),
        adv_recovery_pct:    data.adv_recovery_pct||10,
        retention_pct:       data.retention_pct||5,
        collection_lag_months: data.collection_lag_months||2,
      })
    }
  }

  function buildMonths(start,end){
    const ms=[];const s=new Date(start);s.setDate(1);const e=new Date(end);e.setDate(1)
    while(s<=e){ms.push(monthKey(s));s.setMonth(s.getMonth()+1)}
    return ms
  }

  async function saveSettings(){
    const payload={
      project_id:projectId,
      project_start_month:firstOfMonth(settingsForm.project_start_month),
      project_end_month:  firstOfMonth(settingsForm.project_end_month),
      adv_recovery_pct:   parseFloat(settingsForm.adv_recovery_pct)||10,
      retention_pct:      parseFloat(settingsForm.retention_pct)||5,
      collection_lag_months: parseInt(settingsForm.collection_lag_months)||2,
    }
    const{data:ex}=await supabase.from('cashflow_settings').select('id').eq('project_id',projectId).maybeSingle()
    if(ex) await supabase.from('cashflow_settings').update(payload).eq('project_id',projectId)
    else await supabase.from('cashflow_settings').insert(payload)
    setShowSettings(false); loadSettings(projectId)
  }

  async function loadTotals(pid){
    const{data}=await supabase.from('cashflow_totals').select('*').eq('project_id',pid)
    const map={}
    for(const r of (data||[])) map[r.item_type]={ total_at_completion:parseFloat(r.total_at_completion)||0, till_period_amount:parseFloat(r.till_period_amount)||0, till_period_month:r.till_period_month }
    setTotals(map)
  }

  async function loadDist(pid){
    const{data}=await supabase.from('cashflow_cashin_monthly').select('*').eq('project_id',pid)
    const map={}
    for(const r of (data||[])){
      if(r.item_type==='opening') continue
      if(!map[r.item_type]) map[r.item_type]={}
      map[r.item_type][r.period_month.slice(0,7)]=parseFloat(r.amount)||0
    }
    const opRow=(data||[]).find(r=>r.item_type==='opening')
    setOpeningBalance(parseFloat(opRow?.amount)||0)
    setDist(map)
  }

  async function loadCashOutDist(pid){
    // Read cashflow_monthly_distribution (cc_packages) grouped by payment type
    const{data:dist}=await supabase.from('cashflow_monthly_distribution').select('*').eq('project_id',pid).eq('item_type','cc_package')
    // also get payment_type per cf_package from cf_payment_entries
    const{data:entries}=await supabase.from('cf_payment_entries').select('cf_package,payment_type').eq('project_id',pid)
    const pkgType={}
    for(const e of (entries||[])) if(e.cf_package) pkgType[e.cf_package]=e.payment_type
    // also check dictionary for budget_type to help classify
    const{data:dict}=await supabase.from('cf_package_dictionary').select('cf_package,budget_type').eq('project_id',pid)
    for(const d of (dict||[])) if(!pkgType[d.cf_package]) pkgType[d.cf_package]= d.budget_type==='indirect'?'pay_payroll':'pay_sub_supplier'

    const map={} // { [ym]: {pay_sub_supplier,pay_payroll,pay_others} }
    for(const r of (dist||[])){
      const ym=r.period_month.slice(0,7)
      if(!map[ym]) map[ym]={pay_sub_supplier:0,pay_payroll:0,pay_others:0}
      const pt=pkgType[r.item_id]||'pay_sub_supplier'
      const key = pt==='payroll'?'pay_payroll':pt==='others'?'pay_others':'pay_sub_supplier'
      map[ym][key]=(map[ym][key]||0)+(parseFloat(r.amount)||0)
    }
    setCashOutDist(map)
  }

  async function loadFinanceFees(pid){
    const{data}=await supabase.from('financial_fees_entries').select('*').eq('project_id',pid)
    const map={}
    for(const r of (data||[])){
      const ym=r.period_month.slice(0,7)
      map[ym]=(map[ym]||0)+(parseFloat(r.amount)||0)
    }
    setFinanceFees(map)
  }

  async function loadPkgPayTypes(pid){
    const{data}=await supabase.from('cf_payment_entries').select('cf_package,payment_type').eq('project_id',pid)
    const map={}
    for(const e of (data||[])) if(e.cf_package) map[e.cf_package]=e.payment_type
    setPkgPayTypes(map)
  }

  function handleCell(itemType, ym, val){
    const num=parseFloat(val)||0
    setDist(d=>({...d,[itemType]:{...(d[itemType]||{}),[ym]:num}}))
    setDirty(d=>({...d,[`${itemType}|${ym}`]:true}))
  }

  async function saveAll(){
    setSaving(true)
    const manualRows=[...INVOICED_ROWS,...COLLECT_ROWS]
    for(const itemType of manualRows){
      for(const ym of months){
        const key=`${itemType}|${ym}`
        if(!dirty[key]) continue
        const amt=dist[itemType]?.[ym]||0
        const period=firstOfMonth(ym)
        const{data:ex}=await supabase.from('cashflow_cashin_monthly').select('id').eq('project_id',projectId).eq('item_type',itemType).eq('period_month',period).maybeSingle()
        if(ex) await supabase.from('cashflow_cashin_monthly').update({amount:amt}).eq('id',ex.id)
        else await supabase.from('cashflow_cashin_monthly').insert({project_id:projectId,item_type:itemType,period_month:period,amount:amt})
      }
    }
    // save opening balance as single row (period = first month)
    if(openingDirty && months.length>0){
      const period=firstOfMonth(months[0])
      const{data:ex}=await supabase.from('cashflow_cashin_monthly').select('id').eq('project_id',projectId).eq('item_type','opening').maybeSingle()
      if(ex) await supabase.from('cashflow_cashin_monthly').update({amount:openingBalance,period_month:period}).eq('id',ex.id)
      else await supabase.from('cashflow_cashin_monthly').insert({project_id:projectId,item_type:'opening',period_month:period,amount:openingBalance})
      setOpeningDirty(false)
    }
    setDirty({}); setSaving(false)
  }

  async function saveTotals(){
    const allTypes=[...INVOICED_ROWS,...COLLECT_ROWS]
    for(const t of allTypes){
      if(!totalsForm[t]) continue
      const payload={
        project_id:projectId, item_type:t,
        total_at_completion: parseFloat(totalsForm[t]?.total_at_completion)||0,
        till_period_amount:  parseFloat(totalsForm[t]?.till_period_amount)||0,
        till_period_month:   totalsForm[t]?.till_period_month||null,
      }
      const{data:ex}=await supabase.from('cashflow_totals').select('id').eq('project_id',projectId).eq('item_type',t).maybeSingle()
      if(ex) await supabase.from('cashflow_totals').update(payload).eq('id',ex.id)
      else await supabase.from('cashflow_totals').insert(payload)
    }
    setShowTotalsModal(false); loadTotals(projectId)
  }

  // ── Compute values ──
  function getVal(itemType, ym){
    const s = settings
    const advRecPct  = (s?.adv_recovery_pct||10)/100
    const retPct     = (s?.retention_pct||5)/100
    const lag        = s?.collection_lag_months||2

    if(itemType==='adv_recovery'){
      // = advRecPct × gross_invoice of same month (negative)
      return -((dist['gross_invoice']?.[ym]||0) * advRecPct)
    }
    if(itemType==='retention_ded'){
      return -((dist['gross_invoice']?.[ym]||0) * retPct)
    }
    if(itemType==='net_invoice'){
      const adv   = dist['adv_payment']?.[ym]||0
      const gross = dist['gross_invoice']?.[ym]||0
      const rec   = getVal('adv_recovery',ym)
      const ret   = getVal('retention_ded',ym)
      return adv+gross+rec+ret
    }
    if(itemType==='progress_collection'){
      // Net invoice of (ym - lag months) × 1.15
      const laggedYm = getLaggedMonth(ym, lag)
      const netLagged = laggedYm ? getVal('net_invoice', laggedYm) : 0
      return netLagged * 1.15
    }
    if(itemType==='total_collection'){
      return (dist['adv_collection']?.[ym]||0) + getVal('progress_collection',ym) + (dist['retention_collection']?.[ym]||0)
    }
    if(itemType==='pay_sub_supplier') return -(cashOutDist[ym]?.pay_sub_supplier||0)
    if(itemType==='pay_payroll')      return -(cashOutDist[ym]?.pay_payroll||0)
    if(itemType==='pay_others')       return -(cashOutDist[ym]?.pay_others||0)
    if(itemType==='total_payment')    return getVal('pay_sub_supplier',ym)+getVal('pay_payroll',ym)+getVal('pay_others',ym)
    if(itemType==='finance_fees')     return financeFees[ym]||0
    if(itemType==='net_position')     return getVal('total_collection',ym)+getVal('total_payment',ym)+(financeFees[ym]||0)
    if(itemType==='project_bank'){
      let cum=0
      for(const m of months){
        cum+=getVal('net_position',m)
        if(m===ym) return openingBalance+cum
      }
      return openingBalance+cum
    }
    // manual rows
    return dist[itemType]?.[ym]||0
  }

  function getLaggedMonth(ym, lag){
    const idx = months.indexOf(ym)
    if(idx-lag < 0) return null
    return months[idx-lag]
  }

  function getTillPeriod(itemType){
    return totals[itemType]?.till_period_amount||0
  }

  function getTotalAtCompletion(itemType){
    return totals[itemType]?.total_at_completion||0
  }

  function getRemaining(itemType){
    if(COMPUTED_ROWS.has(itemType)) return null
    const tac = getTotalAtCompletion(itemType)
    const tp  = getTillPeriod(itemType)
    return tac - tp
  }

  function getMonthsTotal(itemType){
    return months.reduce((s,ym)=>s+getVal(itemType,ym),0)
  }

  function getCheck(itemType){
    if(COMPUTED_ROWS.has(itemType)) return null
    const rem = getRemaining(itemType)
    if(rem===null) return null
    const mt  = getMonthsTotal(itemType)
    return mt - rem // should be 0
  }

  // ── Styles ──
  const thS={padding:'7px 8px',fontSize:10,fontWeight:700,background:'#1B3A6B',color:'#fff',whiteSpace:'nowrap',textAlign:'right',position:'sticky',top:0,zIndex:1}
  const thSL={...thS,textAlign:'left',minWidth:200,position:'sticky',left:0,zIndex:3}
  const tdS={padding:'5px 8px',fontSize:11,borderBottom:'1px solid #E5E7EB',textAlign:'right',fontFamily:'monospace',whiteSpace:'nowrap'}
  const tdSL={...tdS,textAlign:'left',position:'sticky',left:0,background:'#fff',zIndex:1}
  const tdSLBold={...tdSL,fontWeight:700}

  function rowBg(itemType){
    if(['net_invoice','total_collection','total_payment'].includes(itemType)) return '#EFF6FF'
    if(itemType==='net_position') return '#1B3A6B'
    if(itemType==='project_bank') return '#0F2A50'
    if(Object.keys(SECTION_HEADERS).includes(itemType)) return '#E8EDF5'
    return '#fff'
  }
  function rowColor(itemType,v){
    if(['net_position','project_bank'].includes(itemType)) return '#fff'
    if(v<0) return '#dc2626'
    if(v>0 && ['net_position','project_bank'].includes(itemType)) return '#fff'
    return '#111'
  }

  function renderCell(itemType, ym){
    const isManual = !COMPUTED_ROWS.has(itemType)
    // adv_recovery & retention_ded auto-computed from gross
    const isAutoComputed = itemType==='adv_recovery'||itemType==='retention_ded'||itemType==='progress_collection'
    const v = getVal(itemType, ym)

    if(!isManual || isAutoComputed){
      return <td key={ym} style={{...tdS,color:v<0?'#dc2626':v>0?'#111':'#9CA3AF',fontWeight:['net_invoice','total_collection','total_payment','net_position','project_bank'].includes(itemType)?700:400,background:rowBg(itemType)==='#fff'?'#F9FAFB':rowBg(itemType)}}>
        {v?fmtC(v):'—'}
      </td>
    }
    return(
      <td key={ym} style={{padding:2,borderBottom:'1px solid #E5E7EB',background:rowBg(itemType)}}>
        <input type="number" value={dist[itemType]?.[ym]||''}
          onChange={e=>handleCell(itemType,ym,e.target.value)}
          style={{width:88,padding:'3px 6px',fontSize:11,border:dirty[`${itemType}|${ym}`]?'1px solid #1B3A6B':'1px solid transparent',borderRadius:4,textAlign:'right',fontFamily:'monospace',background:dirty[`${itemType}|${ym}`]?'#EFF6FF':'transparent'}}/>
      </td>
    )
  }

  function renderRow(itemType){
    // Opening Balance — special single-value row
    if(itemType==='opening'){
      return(
        <tr key="opening" style={{background:'#F8FAFF'}}>
          <td style={{...tdSLBold,background:'#F8FAFF',color:'#1B3A6B',paddingLeft:16}}>Opening Balance</td>
          <td style={{...tdS,background:'#F8FAFF',fontWeight:700,color:'#1B3A6B'}}>{openingBalance?fmtC(openingBalance):'—'}</td>
          <td style={{...tdS,background:'#F8FAFF'}}>—</td>
          <td style={{...tdS,background:'#F8FAFF'}}>—</td>
          {months.map((ym,i)=>(
            <td key={ym} style={{...tdS,background:'#F8FAFF',fontWeight:i===0?700:400,color:i===0?'#1B3A6B':'#9CA3AF'}}>
              {i===0 ? (openingBalance?fmtC(openingBalance):'—') : '—'}
            </td>
          ))}
          <td style={{...tdS,background:'#F8FAFF'}}>—</td>
        </tr>
      )
    }

    const isSection = SECTION_HEADERS[itemType]
    const bg=rowBg(itemType)
    const isSpecial=['net_position','project_bank'].includes(itemType)

    const tillPeriod = getTillPeriod(itemType)
    const totalAtC   = getTotalAtCompletion(itemType)
    const remaining  = getRemaining(itemType)
    const check      = getCheck(itemType)
    const monthsSum  = getMonthsTotal(itemType)

    return(
      <tr key={itemType} style={{background:bg}}>
        <td style={{...tdSLBold, background:bg, color:isSpecial?'#fff':NEGATIVE_ROWS.has(itemType)?'#dc2626':'#111', paddingLeft: isSection?8:16}}>
          {LABELS[itemType]}
        </td>
        {/* Total at Completion */}
        <td style={{...tdS,background:bg,color:isSpecial?'#fff':'#111',fontWeight:isSpecial?700:500}}>
          {COMPUTED_ROWS.has(itemType)
            ? (isSpecial?<span style={{color:'#9CA3AF',fontSize:10}}>—</span>:monthsSum?fmtC(monthsSum):'—')
            : (totalAtC?fmtC(totalAtC):'—')}
        </td>
        {/* Till Period */}
        <td style={{...tdS,background:bg,color:isSpecial?'#fff':'#111'}}>
          {COMPUTED_ROWS.has(itemType)?'—':(tillPeriod?fmtC(tillPeriod):'—')}
        </td>
        {/* Remaining */}
        <td style={{...tdS,background:bg,fontWeight:700,color:isSpecial?'#fff':remaining<0?'#dc2626':'#1B3A6B'}}>
          {remaining!=null?(remaining?fmtC(remaining):'—'):'—'}
        </td>
        {/* Monthly cells */}
        {months.map(ym=>renderCell(itemType,ym))}
        {/* Check */}
        <td style={{...tdS,background:bg,fontWeight:700,color:check==null?'#9CA3AF':Math.abs(check)<1?'#16a34a':'#dc2626',fontSize:10}}>
          {check==null?'—':Math.abs(check)<1?'✓ OK':fmtC(check)}
        </td>
      </tr>
    )
  }

  function renderSectionHeader(label, colspan){
    return(
      <tr key={'sh_'+label}>
        <td colSpan={colspan} style={{padding:'7px 10px',background:'#0F2A50',color:'#fff',fontWeight:800,fontSize:11,letterSpacing:'.05em',textTransform:'uppercase'}}>
          {label}
        </td>
      </tr>
    )
  }

  if(loading) return <div style={{padding:40,textAlign:'center',color:'#6B7280'}}>Loading...</div>

  if(!settings?.project_start_month) return(
    <div style={{padding:40,textAlign:'center'}}>
      <p style={{color:'#6B7280',marginBottom:16}}>حدد أولاً فترة المشروع وإعدادات النسب</p>
      <button onClick={()=>setShowSettings(true)}
        style={{padding:'10px 24px',background:'#1B3A6B',color:'#fff',borderRadius:8,border:'none',cursor:'pointer',fontWeight:700}}>
        ⚙ إعدادات المشروع
      </button>
      {showSettings && renderSettingsModal()}
    </div>
  )

  const colCount = 5 + months.length + 1

  function renderSettingsModal(){
    return(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}}>
        <div style={{background:'#fff',borderRadius:16,padding:32,width:420,boxShadow:'0 20px 60px rgba(0,0,0,.2)'}}>
          <h3 style={{fontSize:16,fontWeight:700,marginBottom:20}}>⚙ إعدادات Cash Flow</h3>
          {[
            {k:'project_start_month',l:'شهر البداية',t:'month'},
            {k:'project_end_month',l:'شهر النهاية',t:'month'},
          ].map(f=>(
            <div key={f.k} style={{marginBottom:14}}>
              <label style={{fontSize:12,color:'#6B7280',display:'block',marginBottom:6}}>{f.l}</label>
              <input type={f.t} value={settingsForm[f.k]}
                onChange={e=>setSettingsForm(p=>({...p,[f.k]:e.target.value}))}
                style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}/>
            </div>
          ))}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:20}}>
            {[
              {k:'adv_recovery_pct',l:'Advance Recovery %'},
              {k:'retention_pct',l:'Retention %'},
              {k:'collection_lag_months',l:'Collection Lag (months)'},
            ].map(f=>(
              <div key={f.k}>
                <label style={{fontSize:11,color:'#6B7280',display:'block',marginBottom:4}}>{f.l}</label>
                <input type="number" value={settingsForm[f.k]}
                  onChange={e=>setSettingsForm(p=>({...p,[f.k]:e.target.value}))}
                  style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'6px 10px',fontSize:13,boxSizing:'border-box'}}/>
              </div>
            ))}
          </div>
          <div style={{display:'flex',gap:10}}>
            <button onClick={()=>setShowSettings(false)} style={{flex:1,padding:'10px',border:'1px solid #E5E7EB',borderRadius:8,cursor:'pointer'}}>إلغاء</button>
            <button onClick={saveSettings} style={{flex:1,padding:'10px',background:'#1B3A6B',color:'#fff',borderRadius:8,border:'none',cursor:'pointer',fontWeight:700}}>حفظ</button>
          </div>
        </div>
      </div>
    )
  }

  function renderTotalsModal(){
    const rows=[...INVOICED_ROWS,...COLLECT_ROWS]
    return(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50,overflowY:'auto'}}>
        <div style={{background:'#fff',borderRadius:16,padding:28,width:600,boxShadow:'0 20px 60px rgba(0,0,0,.2)',margin:'20px auto'}}>
          <h3 style={{fontSize:16,fontWeight:700,marginBottom:20}}>📊 Total at Completion & Till Period</h3>
          <table style={{width:'100%',borderCollapse:'collapse',marginBottom:20}}>
            <thead>
              <tr style={{background:'#F3F4F6'}}>
                <th style={{padding:'8px 10px',fontSize:11,fontWeight:700,textAlign:'left'}}>Item</th>
                <th style={{padding:'8px 10px',fontSize:11,fontWeight:700,textAlign:'right'}}>Total at Completion</th>
                <th style={{padding:'8px 10px',fontSize:11,fontWeight:700,textAlign:'right'}}>Till Period Amount</th>
                <th style={{padding:'8px 10px',fontSize:11,fontWeight:700,textAlign:'center'}}>Till Month</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(t=>(
                <tr key={t} style={{borderBottom:'1px solid #E5E7EB'}}>
                  <td style={{padding:'8px 10px',fontSize:12,fontWeight:600,color:NEGATIVE_ROWS.has(t)?'#dc2626':'#111'}}>{LABELS[t]}</td>
                  <td style={{padding:4}}>
                    <input type="number" value={totalsForm[t]?.total_at_completion??totals[t]?.total_at_completion??''}
                      onChange={e=>setTotalsForm(f=>({...f,[t]:{...(f[t]||totals[t]||{}),total_at_completion:e.target.value}}))}
                      style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:6,padding:'5px 8px',fontSize:12,textAlign:'right',fontFamily:'monospace'}}/>
                  </td>
                  <td style={{padding:4}}>
                    <input type="number" value={totalsForm[t]?.till_period_amount??totals[t]?.till_period_amount??''}
                      onChange={e=>setTotalsForm(f=>({...f,[t]:{...(f[t]||totals[t]||{}),till_period_amount:e.target.value}}))}
                      style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:6,padding:'5px 8px',fontSize:12,textAlign:'right',fontFamily:'monospace'}}/>
                  </td>
                  <td style={{padding:4}}>
                    <input type="month" value={(totalsForm[t]?.till_period_month??totals[t]?.till_period_month??'')?.slice(0,7)}
                      onChange={e=>setTotalsForm(f=>({...f,[t]:{...(f[t]||totals[t]||{}),till_period_month:firstOfMonth(e.target.value)}}))}
                      style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:6,padding:'5px 8px',fontSize:11}}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{display:'flex',gap:10}}>
            <button onClick={()=>setShowTotalsModal(false)} style={{flex:1,padding:'10px',border:'1px solid #E5E7EB',borderRadius:8,cursor:'pointer'}}>إلغاء</button>
            <button onClick={saveTotals} style={{flex:1,padding:'10px',background:'#1B3A6B',color:'#fff',borderRadius:8,border:'none',cursor:'pointer',fontWeight:700}}>💾 حفظ</button>
          </div>
        </div>
      </div>
    )
  }

  const ALL_ROWS = [
    'adv_payment','gross_invoice','adv_recovery','retention_ded','net_invoice',
    'adv_collection','progress_collection','retention_collection','total_collection',
    'pay_sub_supplier','pay_payroll','pay_others','total_payment',
    'finance_fees',
    '__net_section','net_position','opening','project_bank',
  ]

  return(
    <div>
      {/* Actions */}
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <button onClick={()=>setShowSettings(true)}
          style={{padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer'}}>
          ⚙ {settings?.project_start_month?.slice(0,7)} → {settings?.project_end_month?.slice(0,7)} | Rec.%:{settings?.adv_recovery_pct} | Ret.%:{settings?.retention_pct} | Lag:{settings?.collection_lag_months}m
        </button>
        <button onClick={()=>{ setTotalsForm({}); setShowTotalsModal(true) }}
          style={{padding:'6px 14px',background:'#1B3A6B',color:'#fff',borderRadius:6,border:'none',fontSize:12,cursor:'pointer',fontWeight:600}}>
          📊 Total at Completion & Till Period
        </button>
        {/* Opening Balance */}
        <div style={{display:'flex',alignItems:'center',gap:6,background:'#fff',border:'1px solid #E5E7EB',borderRadius:6,padding:'4px 10px'}}>
          <span style={{fontSize:11,color:'#6B7280',whiteSpace:'nowrap'}}>Opening Balance:</span>
          <input type="number"
            value={openingBalance||''}
            onChange={e=>{ setOpeningBalance(parseFloat(e.target.value)||0); setOpeningDirty(true) }}
            placeholder="0"
            style={{width:110,border:'none',outline:'none',fontSize:12,fontFamily:'monospace',fontWeight:700,color:'#1B3A6B',textAlign:'right'}}/>
        </div>
        {(Object.keys(dirty).length>0 || openingDirty) && (
          <button onClick={saveAll} disabled={saving}
            style={{padding:'6px 18px',background:'#16a34a',color:'#fff',borderRadius:6,border:'none',fontSize:12,fontWeight:700,cursor:'pointer'}}>
            {saving?'⏳ Saving...':`💾 Save (${Object.keys(dirty).length + (openingDirty?1:0)})`}
          </button>
        )}
      </div>

      {/* Legend */}
      <div style={{display:'flex',gap:16,marginBottom:12,fontSize:11,color:'#6B7280'}}>
        <span>🔵 Manual entry</span>
        <span>⚙ Auto-computed</span>
        <span>Advance Recovery = {settings?.adv_recovery_pct}% of Gross</span>
        <span>Retention = {settings?.retention_pct}% of Gross</span>
        <span>Collection Lag = {settings?.collection_lag_months} months</span>
      </div>

      <div style={{overflowX:'auto',borderRadius:10,border:'1px solid #E5E7EB',boxShadow:'0 1px 4px rgba(0,0,0,.06)'}}>
        <table style={{borderCollapse:'collapse',width:'100%'}}>
          <thead>
            <tr>
              <th style={{...thSL,minWidth:220,background:'#1B3A6B'}}>Description</th>
              <th style={{...thS,minWidth:110}}>Total at Completion</th>
              <th style={{...thS,minWidth:100}}>Till Period</th>
              <th style={{...thS,minWidth:90}}>Remaining</th>
              {months.map(ym=><th key={ym} style={{...thS,minWidth:90}}>{monthLabel(ym)}</th>)}
              <th style={{...thS,background:'#0F2A50',minWidth:70}}>Check</th>
            </tr>
          </thead>
          <tbody>
            {ALL_ROWS.map(itemType=>{
              const secLabel = SECTION_HEADERS[itemType]
              if(itemType.startsWith('__')) return renderSectionHeader(secLabel, colCount)
              return(
                <>
                  {secLabel && renderSectionHeader(secLabel, colCount)}
                  {renderRow(itemType)}
                </>
              )
            })}
          </tbody>
        </table>
      </div>

      {showSettings && renderSettingsModal()}
      {showTotalsModal && renderTotalsModal()}
    </div>
  )
}
